import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { deleteCookie, setCookie } from 'utils/cookies';
import { authAccountWithIntegration } from 'utils/integrations';
import APModal from 'components/Modal/APModal';
import Cards from 'components/Cards';
import TypeCard from 'components/Cards/TypeCard';
import * as actions from 'containers/App/actions';

import GmailImage from 'assets/images/gmail.svg';
import iCloudImage from 'assets/images/icloud.svg';
import MicrosoftImage from 'assets/images/microsoft.svg';

const useConnectModal = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.global.userInfo);
  const { integrations } = userInfo;

  const {
    deleteIntegrationAccount,
  } = bindActionCreators(actions, dispatch);

  const [type, setType] = useState('');
  const [notEnabledOpen, setNotEnabledOpen] = useState(false);
  const [connectedModalOpen, setConnectedModalOpenState] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // This allows the `connectedCallback` value to be callable
  const [connectedCallback, setConnectedCallback] = useState(() => () => {});

  const integrationsNonVirtual = useMemo(() => integrations
    .filter(integration => {
      return integration.grantStatus !== 'disconnected' && integration.provider !== 'virtual-calendar';
    }), [integrations]);

  const setErrorModal = (message) => {
    setErrorModalMessage(message);
    setErrorModalOpen(true);
  };

  const errorModalCloseHandler = () => {
    setErrorModalOpen(false);
    setErrorModalMessage(null);
  };

  const setRedirectPath = (path) => {
    setCookie('nylas_exchange_redirect_url', path, 10 / 1440); // 10 minutes expiry
  };

  const deleteRedirectPath = () => {
    deleteCookie('nylas_exchange_redirect_url');
  };

  function setConnectedModalOpen(open, callback) {
    setConnectedModalOpenState(open);
    if (callback) {
      setConnectedCallback(() => callback);
    }
  };

  const ConnectModal = () => (
    <>
      <APModal
        title="Heads up!"
        name="not-enabled-calendar-sync"
        open={notEnabledOpen}
        onClose={() => setNotEnabledOpen(false)}
        actions={(
          <button
            type="button"
            className="button button--secondary"
            onClick={() => setNotEnabledOpen(false)}
          >
            Got It
          </button>
        )}
      >
        <p>
          This feature is powered by AP Connect, but it looks like you haven’t been enabled yet.
          Please contact your account owner to request access. Once enabled, come back here to get connected.
        </p>
      </APModal>

      <APModal
        title="You're Connected!"
        name="account-connected"
        open={connectedModalOpen}
        onClose={() => {
          setConnectedModalOpen(false);
          connectedCallback();
          setConnectedCallback(() => () => {});
        }}
        actions={(
          <button
            onClick={() => {
              setConnectedModalOpen(false);
              connectedCallback();
              setConnectedCallback(() => () => {});
            }}
            className="button"
          >
            Got it
          </button>
        )}
      >
        <div>
          <p>AP Connect is now live in your account, unlocking powerful new calendar, scheduling, and email tools:</p>
          <ul>
            <li>
              <strong>Manage Sync Preferences:</strong> Head to your Account Calendar to sync Aisle Planner with
              external calendars&nbsp;&mdash; or bring external events into Aisle Planner.
            </li>
            <li>
              <strong>Simplify Scheduling:</strong> Use the new Scheduling Assistant in your Account Calendar to create
              and share booking pages for easier meeting coordination.
            </li>
            <li>
              <strong>Streamline Communication:</strong> Visit your Lead Manager to send and receive standalone emails
              for each lead, all in one place.
            </li>
          </ul>

          <p>
            <strong>Pro Tip:</strong> Avoid calendar duplicates! Now that you manage project calendar syncing from your
            Account Calendar, visit your external calendar to disconnect any individually synced Aisle Planner project
            calendars.
          </p>
        </div>
      </APModal>
      <APModal
        open={connectModalOpen}
        title="Let’s get you connected."
        subtitle="Select your calendar/email provider below."
        name="connect-provider"
        size="medium"
        onClose={() => setConnectModalOpen(false)}
      >
        <Cards>
          <TypeCard
            selected={type === 'google'}
            name="gmail"
            title="Google"
            img={{
              imgUrl: GmailImage,
              alt: 'Google',
            }}
            onClick={() => setType('google')}
          />
          <TypeCard
            selected={type === 'icloud'}
            name="icloud"
            title="Apple iCloud"
            img={{
              imgUrl: iCloudImage,
              alt: 'Apple iCloud',
            }}
            onClick={() => setType('icloud')}
          />
          <TypeCard
            selected={type === 'microsoft'}
            name="microsoft"
            title="Microsoft"
            img={{
              imgUrl: MicrosoftImage,
              alt: 'Microsoft',
            }}
            onClick={() => setType('microsoft')}
          />
        </Cards>
        <button
          className="button"
          onClick={() => authAccountWithIntegration(
            { 'provider': type, 'baseUrl': window.location.origin },
            setErrorModal,
          )}
          disabled={!type}
        >
          Continue
        </button>
      </APModal>

      <APModal
        open={errorModalOpen}
        onClose={errorModalCloseHandler}
        actions={(
          <button
            onClick={errorModalCloseHandler}
            className="button"
          >
            Ok
          </button>
        )}
      >
        <div>{errorModalMessage}</div>
      </APModal>

      <APModal
        title="Disconnect Your External Account"
        name="confirm-disconnect"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        actions={(
          <>
            <button
              onClick={() => setConfirmModalOpen(false)}
              className="button button--cancel"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                deleteIntegrationAccount(integrationsNonVirtual[0].id);
                setConfirmModalOpen(false);
              }}
              className="button"
            >
              Disconnect
            </button>
          </>
        )}
      >
        <p>
          Your scheduling, calendar and email integrations are all enabled by the external account you linked to Aisle
          Planner. By disconnecting, any features enabled by this integration will no longer be active. Are you sure you
          want to disconnect?
        </p>
      </APModal>
    </>
  );

  return {
    connectedModalOpen,
    setConnectedModalOpen,
    connectModalOpen,
    setConnectModalOpen,
    confirmModalOpen,
    setConfirmModalOpen,
    errorModalOpen,
    setErrorModalOpen,
    errorModalMessage,
    setErrorModalMessage,
    ConnectModal,
    setErrorModal,
    integrationsNonVirtual,
    setRedirectPath,
    deleteRedirectPath,
    setNotEnabledOpen,
  };
};

export default useConnectModal;
